import React, {useCallback} from 'react';
import {Link} from 'react-router-dom';

import {BulkActionFooter} from 'src/components/bulk-tool/bulk-action-footer';
import {ScrollableTable} from 'src/components/scrollable-table';
import {WidthItem} from 'src/components/width-item';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {TOOL_TITLE} from 'src/modules/bulk-update/update-admin-role';

import Button from '@pb/kit-component-button';
import {DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import Label from '@pb/kit-component-label';
import {Tooltip} from '@pb/kit-component-popups';
import {Cell, Row} from '@pb/kit-component-table';


import {AdminSuggest} from './admin-suggest';
import {useAssignUser} from './useAssignUser';

import type {ActionOptions} from 'src/components/save-with-reason/action-with-reason-button';

const DESCRIPTION = 'This functionality allows assignment of the selected admin role to a user.' +
    ' Please be aware that only one admin role can be assigned to a user so' +
    ' this action will replace any existing admin roles assigned to the user previously.';

type Props = {
    profileId: number,
    onClose: () => void,
    onDone: () => void,
    onSubmit: () => void,
    onPendingChange: (pending: boolean) => void,
};

const UNMATCHED_EMAIL_TOOLTIP = 'No user found matching this email address';

export const AssignUserModal: React.FC<Props> = ({profileId, onClose, onDone, onSubmit, onPendingChange}) => {
    const {
        assignedUsers,
        assignUser,
        assignUsers,
        removeUser,
        saveUsers,
    } = useAssignUser({onDone, roleId: profileId});

    const {roleUpdate} = useEventTracking();

    const onSaveUsers = useCallback(async (options?: ActionOptions) => {
        roleUpdate({
            parameterName: 'Update Assigned Users',
            targetRoleId: profileId,
        });

        const error = await saveUsers(options);
        if (error) {
            return error;
        }
        onSubmit();
    }, [roleUpdate, saveUsers, onSubmit, profileId]);

    const isSaveDisabled = !assignedUsers.length || assignedUsers.some(({userInfo}) => userInfo.userId < 0);

    return (
        <>
            <DialogHeader onClose={onClose}>
                {TOOL_TITLE}
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="s">
                    <div>{DESCRIPTION}</div>
                    <WidthItem width="100%">
                        <Label>Users</Label>
                        <AdminSuggest
                            id="assign-user-suggest"
                            profileId={profileId}
                            addedMembers={assignedUsers}
                            onAdd={assignUser}
                            onBulkAdd={assignUsers}
                        />
                    </WidthItem>
                    {!!(assignedUsers.length) && (
                        <ScrollableTable
                            maxBodyHeight={235}
                            head={[
                                {cell: '', props: {width: 32}},
                                {cell: 'First Name'},
                                {cell: 'Last Name'},
                                {cell: 'Email', props: {width: 235}},
                                {cell: 'Current Role', props: {width: 200}},
                                {cell: '', props: {width: 60}},
                            ]}
                            body={(
                                <>
                                    {assignedUsers.map((user, i) => (
                                        <Row key={i}>
                                            <Cell block={true}>
                                                {user.userInfo.userId < 0 && (
                                                    <Tooltip
                                                        color="light"
                                                        anchor={({getAnchorProps, anchorRef}) => (
                                                            <span ref={anchorRef} {...getAnchorProps()}>
                                                                <Icon type={Icons.ERROR}/>
                                                            </span>
                                                        )}>
                                                        {UNMATCHED_EMAIL_TOOLTIP}
                                                    </Tooltip>
                                                )}
                                            </Cell>
                                            <Cell>
                                                <EllipsisTextHint title={user.userInfo.firstName}>
                                                    {user.userInfo.firstName}
                                                </EllipsisTextHint>
                                            </Cell>
                                            <Cell>
                                                <EllipsisTextHint title={user.userInfo.lastName}>
                                                    {user.userInfo.lastName}
                                                </EllipsisTextHint>
                                            </Cell>
                                            <Cell>
                                                <EllipsisTextHint title={user.userInfo.email}>
                                                    {user.userInfo.email}
                                                </EllipsisTextHint>
                                            </Cell>
                                            <Cell>
                                                {user.currentProfile ? (
                                                    <Link
                                                        to={`/roles/${user.currentProfile.profileId}`}
                                                        target="_blank">
                                                        <EllipsisTextHint title={user.currentProfile.profileName}>
                                                            {user.currentProfile.profileName}
                                                        </EllipsisTextHint>
                                                    </Link>
                                                ) : '--'}
                                            </Cell>
                                            <Cell verticalAlign="middle">
                                                <Tooltip
                                                    color="light"
                                                    anchor={({anchorRef, getAnchorProps}) => (
                                                        <BasicIconButton
                                                            ref={anchorRef}
                                                            {...getAnchorProps()}
                                                            iconProps={{type: Icons.TRASH}}
                                                            onClick={() => removeUser(i)}
                                                        />
                                                    )}>
                                                    Remove user
                                                </Tooltip>
                                            </Cell>
                                        </Row>
                                    ))}
                                </>
                            )}
                        />
                    )}
                </Group>
            </DialogMain>
            <BulkActionFooter onPendingChange={onPendingChange} action="Save" onConfirm={onSaveUsers} disabled={isSaveDisabled}>
                <Group>

                    <Button variant="Type.SECONDARY" onClick={onClose}>Cancel</Button>
                </Group>
            </BulkActionFooter>
        </>
    );
};
